import React, { useMemo } from 'react'
import { Box } from '@mui/material'

import { font } from 'settings/tokens'
import { PROSE_MAX_WIDTH } from 'constants/layout'
import { sanitizeCustomFieldHtml } from 'utils/certificateHtml'

interface ExperienceTabContentProps {
  /** `product.experience_content` — brand-authored HTML, UNSANITIZED as stored. */
  content?: string | null
}

/**
 * VRG-782 — the brand's own narrative about this product, as its own screen.
 *
 * ## What this is for, and what keeps it from becoming a brochure
 *
 * The record above it answers "is this real": product, certificate id, status, the
 * Vera mark, provenance. This answers "what is it" in the brand's voice — the making
 * of it, the material, how to care for it. Those are different questions and they
 * want different surfaces, which is the argument for a separate screen rather than
 * more rows on the certificate.
 *
 * The separation is also what makes the content safe to show at all. Brand copy on
 * the record itself borrows the record's credibility — a claim sitting between the
 * certificate id and the Vera mark reads as verified, and none of it is. One tap away,
 * under the brand's own heading, it is plainly the brand speaking.
 *
 * ## Sanitized at render, with the stricter allow-list
 *
 * `sanitizeCustomFieldHtml`, not `sanitizeProductDescription`: no `<a>`, no images, no
 * tables. The description's allow-list is wider because it predates the gate and
 * already carries links a brand typed; this slot is new, so it starts closed. In
 * particular NO LINKS — an anchor here would be a brand-controlled outbound jump from
 * a page that also carries the owner's session, and the certificate's one sanctioned
 * outbound link is the `link` custom field, which prints its destination host next to
 * it (VRG-104) precisely so a reader can see where it goes.
 *
 * Headings are capped at body size for the same reason they are in the custom-field
 * slot: an `<h1>` pasted out of a CMS would otherwise outrank the product title.
 */
export const ExperienceTabContent: React.FC<ExperienceTabContentProps> = ({ content }) => {
  const html = useMemo(() => sanitizeCustomFieldHtml(content), [content])

  // The row that leads here renders only when there is text, so an empty body means
  // someone reached the route directly. Nothing rather than an empty frame.
  if (!html) return null

  return (
    <Box
      sx={{
        // A MEASURE on the prose, not on the page container — this is the one surface
        // in the certificate that is genuinely long-form, and working-conventions §29's
        // note on CONTENT_MAX_WIDTH is explicit that the honest instrument for prose is
        // a cap on the prose itself.
        maxWidth: PROSE_MAX_WIDTH,
        fontSize: font.body,
        lineHeight: 1.6,
        '& p': { margin: 0, marginBottom: '1em' },
        '& p:last-child': { marginBottom: 0 },
        '& ul, & ol': { margin: 0, marginBottom: '1em', paddingLeft: '1.25em' },
        '& li': { marginBottom: '0.35em' },
        '& strong': { fontWeight: 600 },
        // Headings survive as emphasis, not as scale. See the note above.
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontSize: font.body,
          fontWeight: 600,
          margin: 0,
          marginBottom: '0.5em',
        },
        '& blockquote': {
          margin: 0,
          marginBottom: '1em',
          paddingLeft: '1em',
          borderLeft: theme => `2px solid ${theme.palette.divider}`,
          color: 'text.secondary',
        },
        // `data-align` is the one attribute the custom-field allow-list keeps, so the
        // editor's alignment survives the strip. See `certificateHtml`.
        '& [data-align="center"]': { textAlign: 'center' },
        '& [data-align="right"]': { textAlign: 'right' },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

export default ExperienceTabContent
